import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { ProductDetail } from '@/mocks/productDetails';

function rowToProduct(row: Record<string, unknown>): ProductDetail {
  return {
    id: Number(row.id),
    nameAr: String(row.name_ar ?? ''),
    category: String(row.category ?? 'PS5'),
    badge: String(row.badge ?? ''),
    rating: Number(row.rating ?? 5),
    reviews: Number(row.reviews ?? 0),
    price: Number(row.price ?? 0),
    originalPrice: Number(row.original_price ?? 0),
    shortDesc: String(row.short_desc ?? ''),
    longDesc: String(row.long_desc ?? ''),
    images: Array.isArray(row.images) ? (row.images as string[]) : [],
    highlights: Array.isArray(row.highlights) ? (row.highlights as string[]) : [],
    specs: Array.isArray(row.specs) ? (row.specs as { label: string; value: string }[]) : [],
    inBox: Array.isArray(row.in_box) ? (row.in_box as string[]) : [],
    relatedIds: Array.isArray(row.related_ids) ? (row.related_ids as number[]) : [],
  };
}

/** Read-only hook — fetches products from Supabase */
export function useProducts() {
  const [products, setProducts] = useState<ProductDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetched, setFetched] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) {
        setProducts(data.map(rowToProduct));
      }
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getById = useCallback((id: number) => products.find(p => p.id === id), [products]);

  return { products, loading: loading || !fetched, getById, refetch: fetchProducts };
}
